import { EditableText, defineBlock } from "eddev/blocks"
import { cn, tv } from "@utils/tw"
import { Heading } from "@components/atoms/Heading"
import { Button } from "@components/atoms/Button"
import { useMeasure } from "react-use"
import { CLOSE, INFO } from "@components/atoms/Icons"
import { Swiper, SwiperSlide } from "@components/atoms/Swiper"
import { ComponentPropsWithoutRef, forwardRef, ReactNode, useState } from "react"
import { kebabCase } from "@utils/kebabCase"
import { useIsMobile } from "@hooks/useViewportDerived"

export const meta: BlockMeta = {
  title: "Data Dashboards",
  category: "common",
  icon: "welcome-widgets-menus",
  keywords: ["dashboard"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

export default defineBlock("home/data-dashboards", (props) => {
  return (
    <div className="grid-auto">
      <div className="col-span-12">
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .dashboard-swiper {
              overflow: visible !important;
              padding-bottom: 20px;

              div[class^='swiper-button'] {
                top: auto;
                bottom: 0;
                right: 0;
                left: auto;
                width: 27px;
                height: 27px;
                color: #222222;
                box-shadow: inset 0px 0px 0px 1px #222222;
                border-radius: 6px;
                transition: all .35s cubic-bezier(.19,1,.22,1);

                &:after {
                  font-size: 16px !important;
                }

                svg {
                  width: 5px;
                  height: auto;

                  path {
                    stroke: currentColor;
                    stroke-width: 2px;
                  }
                }

                &:hover {
                  background: #5E5E5E;
                  box-shadow: inset 0px 0px 0px 1px #5E5E5E;
                  color: #FFFFFF;
                }

                &:active {
                  background: #222222;
                  box-shadow: inset 0px 0px 0px 1px #222222;
                  color: #FFFFFF;
                }

                &:disabled {
                  background: transparent;
                  box-shadow: inset 0px 0px 0px 1px #8E8E8E;
                  color: #8E8E8E;
                }
              }

              .swiper-button-prev {
                margin-right: 36px;
              }
            }

            @media only screen and (min-width: 1080px) {
              .dashboard-swiper {
                padding-bottom: 0;

                div[class^='swiper-button'] {
                  display: none;
                }
              }
            }
          `,
          }}
        />
        <Swiper
          className="dashboard-swiper"
          autoplay={{
            delay: 5000,
          }}
          navigation
          breakpoints={{
            1080: {
              slidesPerView: 3,
              initialSlide: 0,
              centeredSlides: false,
              spaceBetween: 24,
            },
            800: {
              slidesPerView: 2.25,
              initialSlide: 0,
              centeredSlides: false,
              spaceBetween: 24,
            },
            0: {
              slidesPerView: 1.1,
              initialSlide: 1,
              centeredSlides: true,
              spaceBetween: 16,
            },
          }}
        >
          <SwiperSlide>
            <TileContainer
              animation={<TileRoadSafety />}
              title={"Road Safety"}
              url="/research-data/road-safety/"
              latest="/publication/benchmarking-the-performance-of-the-nrss/"
            />
          </SwiperSlide>

          <SwiperSlide>
            <TileContainer
              animation={<TileAffordability />}
              title={"Transport Affordability"}
              url="/research-data/transport-affordability/"
              latest="/publication/transport-affordability-index/"
            />
          </SwiperSlide>

          <SwiperSlide>
            <TileContainer
              animation={<TileEV />}
              title={"Electric Vehicle Index"}
              url="/research-data/electric-vehicle/"
              latest="/publication/electric-vehicle-index/"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  )
})

export const TileContainer = ({
  animation,
  title,
  url,
  latest,
  autoSize,
  isSearch,
}: {
  animation: ReactNode
  title: string
  url: string
  latest?: string
  autoSize?: boolean
  isSearch?: boolean
}) => {
  const [tileRef, tileSpecs] = useMeasure<HTMLDivElement>()
  const [infoOpen, setInfoOpen] = useState<boolean>(false)
  const [headerRef, headerSpecs] = useMeasure<HTMLDivElement>()
  const isMobile = useIsMobile()

  return (
    <DashboardTile
      isSearch={isSearch}
      ref={tileRef}
      style={{ height: autoSize !== false ? `${tileSpecs?.width}px` : undefined }}
    >
      <InnerTile fade={infoOpen}>
        <Header ref={headerRef} className="gap-2">
          <EditableText
            store={`${kebabCase(title)}_subtitle`}
            as="h5"
            className="!type-label-caps !text-white/50"
            defaultValue="Interactive data report"
            placeholder="Interactive data report"
          />
          <Heading as="h3" variant="h3" className="!text-white">
            {title}
          </Heading>
          <Info onClick={() => setInfoOpen(!infoOpen)}>{infoOpen ? <>{CLOSE}</> : <>{INFO}</>}</Info>
        </Header>
        {animation}
        <ButtonGroup>
          {latest && (
            <Button color="outlineReversed" size={isMobile ? "small" : "medium"} href={latest}>
              Latest report
            </Button>
          )}
          <Button
            color="outlineReversed"
            size={isMobile ? "small" : "medium"}
            iconStyle="right"
            icon="GRAPH"
            href={url}
          >
            See the data
          </Button>
        </ButtonGroup>
      </InnerTile>
      <InfoTile
        open={infoOpen}
        style={{ height: `calc(${tileSpecs?.height - headerSpecs?.height}px - var(--spacing-5) - var(--spacing-5))` }}
      >
        <EditableText as="p" store={`${kebabCase(title)}_info`} placeholder="Enter description..." />
      </InfoTile>
    </DashboardTile>
  )
}

const Header = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full flex flex-col", className)} {...props} />
))
Header.displayName = "Header"

export const TileRoadSafety = () => (
  <AnimationContainer>
    <div className="background">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="243" y="164" width="22" height="114" transform="rotate(90 243 164)" fill="white" />
        <path
          d="M186 19C211.957 19 233 40.0426 233 66L233 161L139 161L139 66C139 40.0426 160.043 19 186 19V19Z"
          fill="white"
        />
        <line x1="121.209" y1="185.709" x2="220.159" y2="185.709" stroke="#8E8E8E" strokeWidth="2.15109" />
        <line x1="222.31" y1="185.709" x2="263.18" y2="185.709" stroke="#8E8E8E" strokeWidth="2.15109" />
        <line x1="104" y1="185.709" x2="114.755" y2="185.709" stroke="#8E8E8E" strokeWidth="2.15109" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M133.624 158.637C136.003 158.637 137.929 160.566 137.929 162.945C137.929 163.066 137.924 163.184 137.915 163.303C138.025 163.257 138.137 163.215 138.25 163.176C140.513 162.443 142.941 163.681 143.678 165.943C144.411 168.206 143.176 170.634 140.913 171.371C140.797 171.407 140.684 171.438 140.568 171.466C140.647 171.554 140.72 171.648 140.791 171.746C142.188 173.67 141.762 176.364 139.837 177.761C137.913 179.157 135.22 178.731 133.823 176.807C133.752 176.711 133.687 176.61 133.626 176.51C133.563 176.612 133.497 176.711 133.426 176.807C132.029 178.731 129.336 179.157 127.411 177.761C125.487 176.364 125.061 173.67 126.458 171.746C126.529 171.648 126.602 171.554 126.68 171.466C126.565 171.438 126.451 171.407 126.337 171.371C124.075 170.633 122.838 168.204 123.571 165.942C124.308 163.68 126.736 162.441 128.999 163.175C129.112 163.212 129.224 163.256 129.334 163.301C129.324 163.183 129.32 163.064 129.32 162.943C129.318 160.566 131.245 158.637 133.624 158.637Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M133.626 164.23C132.438 164.23 132.95 167.48 133.626 168.447C134.299 167.48 134.811 164.23 133.626 164.23Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M133.147 168.794C132.438 167.854 129.505 166.36 129.138 167.491C128.771 168.62 132.019 169.138 133.147 168.794Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M130.851 172.766C131.813 173.462 133.308 170.532 133.328 169.355C132.216 169.739 129.891 172.067 130.851 172.766Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M133.92 169.355C133.94 170.532 135.436 173.462 136.397 172.763C137.357 172.067 135.032 169.739 133.92 169.355Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M138.112 167.491C137.743 166.361 134.811 167.854 134.103 168.794C135.229 169.138 138.477 168.62 138.112 167.491Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M229.735 135C231.527 135 232.979 136.454 232.979 138.246C232.979 138.337 232.975 138.427 232.968 138.516C233.052 138.482 233.136 138.45 233.221 138.421C234.926 137.868 236.756 138.801 237.311 140.506C237.864 142.211 236.933 144.041 235.228 144.596C235.141 144.623 235.056 144.647 234.968 144.668C235.027 144.734 235.083 144.805 235.136 144.879C236.188 146.329 235.867 148.359 234.417 149.411C232.967 150.464 230.937 150.143 229.885 148.693C229.832 148.621 229.782 148.544 229.736 148.469C229.689 148.545 229.639 148.621 229.586 148.693C228.533 150.143 226.503 150.464 225.053 149.411C223.603 148.359 223.282 146.329 224.335 144.879C224.388 144.805 224.443 144.734 224.502 144.668C224.415 144.647 224.33 144.623 224.243 144.596C222.539 144.04 221.606 142.21 222.159 140.505C222.714 138.8 224.545 137.867 226.249 138.42C226.335 138.448 226.419 138.481 226.502 138.515C226.494 138.426 226.491 138.336 226.491 138.245C226.49 136.454 227.942 135 229.735 135Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M229.736 139.214C228.841 139.214 229.227 141.664 229.736 142.392C230.243 141.664 230.63 139.214 229.736 139.214Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M229.375 142.654C228.841 141.946 226.631 140.82 226.354 141.672C226.078 142.523 228.525 142.913 229.375 142.654Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M227.645 145.648C228.37 146.172 229.497 143.964 229.512 143.077C228.674 143.367 226.922 145.121 227.645 145.648Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M229.958 143.077C229.973 143.964 231.1 146.172 231.825 145.646C232.548 145.121 230.796 143.367 229.958 143.077Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M233.117 141.672C232.839 140.821 230.63 141.946 230.096 142.654C230.945 142.913 233.392 142.523 233.117 141.672Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M194.93 160.972C198.056 160.972 200.588 163.508 200.588 166.633C200.588 166.792 200.581 166.948 200.569 167.104C200.714 167.044 200.861 166.988 201.01 166.938C203.983 165.973 207.176 167.601 208.143 170.574C209.108 173.548 207.484 176.74 204.51 177.708C204.358 177.755 204.209 177.797 204.057 177.834C204.16 177.949 204.256 178.072 204.349 178.201C206.185 180.73 205.625 184.271 203.096 186.107C200.567 187.943 197.027 187.382 195.191 184.854C195.098 184.728 195.012 184.595 194.932 184.463C194.85 184.596 194.762 184.728 194.669 184.854C192.834 187.382 189.293 187.943 186.764 186.107C184.235 184.271 183.675 180.73 185.511 178.201C185.604 178.072 185.7 177.949 185.803 177.834C185.651 177.797 185.502 177.755 185.352 177.708C182.378 176.738 180.753 173.546 181.717 170.573C182.685 167.599 185.877 165.972 188.85 166.936C188.999 166.985 189.146 167.043 189.291 167.102C189.277 166.946 189.272 166.791 189.272 166.631C189.27 163.508 191.803 160.972 194.93 160.972Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M199.008 170.048C197.921 168.928 195.323 172.385 195.03 173.908C196.557 173.657 200.093 171.166 199.008 170.048Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M194.263 173.773C194.502 172.245 193.227 168.11 191.824 168.798C190.424 169.486 192.907 173.024 194.263 173.773Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M188.415 175.243C188.638 176.787 192.771 175.516 193.9 174.457C192.52 173.761 188.196 173.697 188.415 175.243Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M194.442 175.016C193.35 176.112 191.954 180.205 193.493 180.472C195.029 180.741 195.097 176.417 194.442 175.016Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M200.037 177.265C200.765 175.883 196.673 174.483 195.138 174.675C195.845 176.052 199.306 178.642 200.037 177.265Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M219.491 19C222.617 19 225.149 21.5359 225.149 24.6616C225.149 24.8209 225.142 24.9766 225.13 25.1324C225.275 25.0729 225.422 25.0169 225.571 24.9661C228.544 24.0018 231.737 25.6294 232.704 28.6028C233.669 31.5763 232.045 34.7685 229.071 35.7363C228.919 35.7835 228.77 35.8255 228.618 35.8623C228.721 35.9778 228.818 36.1003 228.91 36.2298C230.746 38.7587 230.186 42.2992 227.657 44.1351C225.128 45.9709 221.588 45.4109 219.752 42.882C219.659 42.756 219.573 42.623 219.493 42.4917C219.411 42.6247 219.323 42.756 219.23 42.882C217.395 45.4109 213.854 45.9709 211.325 44.1351C208.796 42.2992 208.236 38.7587 210.072 36.2298C210.165 36.1003 210.261 35.9778 210.364 35.8623C210.212 35.8255 210.063 35.7835 209.913 35.7363C206.939 34.7667 205.314 31.5745 206.278 28.6011C207.246 25.6277 210.438 24.0001 213.411 24.9644C213.56 25.0134 213.707 25.0711 213.852 25.1306C213.838 24.9749 213.833 24.8191 213.833 24.6598C213.831 21.5359 216.364 19 219.491 19Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M223.569 28.0767C222.482 26.9562 219.884 30.4131 219.591 31.9359C221.118 31.6856 224.654 29.1947 223.569 28.0767Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M218.824 31.8018C219.063 30.2732 217.788 26.1386 216.385 26.8265C214.985 27.5144 217.468 31.0522 218.824 31.8018Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M212.976 33.2712C213.199 34.8155 217.332 33.544 218.461 32.4856C217.081 31.7889 212.757 31.7257 212.976 33.2712Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M219.003 33.0446C217.911 34.1407 216.515 38.2335 218.054 38.5007C219.59 38.769 219.658 34.4457 219.003 33.0446Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M224.598 35.293C225.326 33.9117 221.234 32.5117 219.699 32.7037C220.406 34.0808 223.867 36.6706 224.598 35.293Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M238.726 150.216C241.035 150.216 242.905 152.089 242.905 154.397C242.905 154.515 242.9 154.63 242.89 154.745C242.998 154.701 243.106 154.66 243.216 154.622C245.412 153.91 247.769 155.112 248.484 157.308C249.196 159.504 247.997 161.861 245.801 162.576C245.689 162.611 245.579 162.642 245.466 162.669C245.543 162.754 245.614 162.845 245.682 162.94C247.038 164.808 246.624 167.422 244.757 168.778C242.889 170.134 240.275 169.72 238.919 167.853C238.85 167.76 238.787 167.661 238.728 167.565C238.667 167.663 238.602 167.76 238.534 167.853C237.178 169.72 234.563 170.134 232.696 168.778C230.828 167.422 230.415 164.808 231.77 162.94C231.839 162.845 231.91 162.754 231.986 162.669C231.874 162.642 231.764 162.611 231.653 162.576C229.457 161.86 228.256 159.502 228.968 157.306C229.683 155.111 232.04 153.909 234.236 154.621C234.346 154.657 234.455 154.7 234.562 154.744C234.552 154.629 234.548 154.514 234.548 154.396C234.547 152.089 236.417 150.216 238.726 150.216Z"
          fill="#8E8E8E"
        />
        <circle cx="238.715" cy="159.909" r="3.48957" fill="#222222" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M220.159 153.222C217.994 151.334 214.44 152.77 214.44 155.612C214.44 156.3 214.662 156.954 215.082 157.508C215.082 157.508 217.502 160.678 218.881 162.485C219.194 162.298 219.556 162.181 219.943 162.144V159.768C219.943 159.653 220.038 159.559 220.159 159.559C220.279 159.559 220.378 159.653 220.378 159.768V162.142C220.765 162.179 221.13 162.297 221.439 162.486C222.812 160.683 225.238 157.506 225.238 157.506C225.66 156.952 225.881 156.298 225.881 155.611C225.883 152.773 222.333 151.334 220.159 153.222Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M208.982 160.941C206.42 162.341 206.767 165.975 209.606 166.852C210.291 167.064 211.017 167.065 211.702 166.858C211.702 166.858 215.621 165.65 217.857 164.96C217.761 164.619 217.754 164.258 217.841 163.895L215.467 163.163C215.354 163.126 215.29 163.008 215.328 162.9C215.364 162.792 215.488 162.731 215.601 162.768L217.979 163.503C218.131 163.163 218.362 162.87 218.65 162.647C217.271 160.85 214.841 157.673 214.841 157.673C214.416 157.121 213.827 156.722 213.142 156.507C210.301 155.63 207.771 158.388 208.982 160.941Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M213.249 173.431C213.813 176.156 217.599 176.997 219.357 174.695C219.782 174.141 220.006 173.487 220.01 172.803C220.01 172.803 220.013 168.884 220.013 166.655C219.645 166.634 219.281 166.526 218.945 166.338L217.476 168.257C217.406 168.351 217.271 168.372 217.172 168.305C217.073 168.235 217.051 168.106 217.125 168.015L218.593 166.093C218.302 165.849 218.079 165.55 217.947 165.222C215.72 165.911 211.794 167.126 211.794 167.126C211.11 167.339 210.525 167.746 210.102 168.302C208.339 170.606 210.368 173.778 213.249 173.431Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M227.068 173.431C229.963 173.769 231.977 170.605 230.217 168.302C229.795 167.748 229.209 167.341 228.528 167.126C228.528 167.126 224.609 165.914 222.373 165.224C222.24 165.55 222.022 165.848 221.731 166.091L223.199 168.014C223.269 168.104 223.246 168.233 223.152 168.303C223.053 168.37 222.918 168.348 222.847 168.256L221.376 166.336C221.043 166.522 220.674 166.632 220.304 166.653C220.311 168.875 220.311 172.801 220.311 172.801C220.314 173.486 220.539 174.14 220.964 174.694C222.723 177.003 226.536 176.139 227.068 173.431Z"
          fill="#FEC32C"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M231.339 160.941C232.563 158.421 230.023 155.63 227.178 156.507C226.49 156.72 225.904 157.121 225.479 157.673C225.479 157.673 223.057 160.844 221.674 162.65C221.958 162.873 222.188 163.163 222.343 163.503L224.717 162.768C224.831 162.731 224.955 162.792 224.994 162.9C225.032 163.008 224.968 163.126 224.855 163.163L222.477 163.895C222.563 164.256 222.556 164.621 222.462 164.961C224.691 165.646 228.62 166.857 228.62 166.857C229.305 167.065 230.028 167.065 230.716 166.85C233.56 165.972 233.887 162.286 231.339 160.941Z"
          fill="#FEC32C"
        />
        <ellipse
          cx="160.654"
          cy="177.903"
          rx="3.22663"
          ry="6.45326"
          transform="rotate(-57.6876 160.654 177.903)"
          fill="#8E8E8E"
        />
        <ellipse
          cx="171.272"
          cy="171.85"
          rx="3.22663"
          ry="6.45326"
          transform="rotate(-129.139 171.272 171.85)"
          fill="#8E8E8E"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M166.966 143.763C170.092 143.763 172.624 146.299 172.624 149.425C172.624 149.584 172.617 149.74 172.605 149.895C172.75 149.836 172.897 149.78 173.046 149.729C176.02 148.765 179.212 150.392 180.18 153.366C181.144 156.339 179.52 159.531 176.546 160.499C176.394 160.546 176.245 160.588 176.093 160.625C176.196 160.741 176.293 160.863 176.385 160.993C178.221 163.522 177.661 167.062 175.132 168.898C172.603 170.734 169.063 170.174 167.227 167.645C167.134 167.519 167.049 167.386 166.968 167.255C166.886 167.388 166.798 167.519 166.706 167.645C164.87 170.174 161.329 170.734 158.8 168.898C156.271 167.062 155.711 163.522 157.547 160.993C157.64 160.863 157.736 160.741 157.839 160.625C157.687 160.588 157.538 160.546 157.388 160.499C154.415 159.53 152.789 156.337 153.753 153.364C154.721 150.391 157.913 148.763 160.886 149.727C161.035 149.776 161.182 149.834 161.327 149.894C161.313 149.738 161.308 149.582 161.308 149.423C161.306 146.299 163.839 143.763 166.966 143.763Z"
          fill="#FEC32C"
        />
        <circle cx="166.951" cy="156.889" r="4.72529" fill="#222222" />
        <circle cx="186" cy="65" r="12" fill="#222222" />
        <rect x="159" y="103" width="54" height="6" rx="3" fill="#222222" />
        <rect x="159" y="122" width="54" height="6" rx="3" fill="#222222" />
      </svg>
    </div>

    <div className="layer-3 rs-up alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="258" y="32" width="24" height="24" rx="12" fill="#C62E1D" />
        <path
          d="M273.1 42.9836C273.344 43.2277 273.74 43.2277 273.984 42.9836C274.228 42.7396 274.228 42.3438 273.984 42.0998L270.65 38.7664C270.528 38.6444 270.368 38.5834 270.208 38.5834C270.124 38.5834 270.043 38.6002 269.969 38.6308C269.895 38.6613 269.826 38.7065 269.766 38.7664L266.433 42.0998C266.189 42.3438 266.189 42.7396 266.433 42.9836C266.677 43.2277 267.073 43.2277 267.317 42.9836L269.583 40.7173L269.583 48.7917C269.583 49.1369 269.863 49.4167 270.208 49.4167C270.554 49.4167 270.833 49.1369 270.833 48.7917L270.833 40.7173L273.1 42.9836Z"
          fill="white"
        />
      </svg>
    </div>

    <div className="layer-2 rs-up alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle opacity="0.55" cx="270" cy="66" r="6" fill="#C62E1D" />
      </svg>
    </div>

    <div className="layer-1 rs-up alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle opacity="0.35" cx="270" cy="79" r="3" fill="#C62E1D" />
      </svg>
    </div>

    <div className="layer-3 rs-down alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="86" y="116" width="24" height="24" rx="12" fill="#4C6B1F" />
        <path
          d="M95.3169 129.016C95.0729 128.772 94.6771 128.772 94.4331 129.016C94.189 129.26 94.189 129.656 94.4331 129.9L97.7664 133.234C97.8884 133.356 98.0484 133.417 98.2083 133.417C98.2931 133.417 98.3739 133.4 98.4476 133.369C98.5213 133.339 98.5904 133.294 98.6503 133.234L101.984 129.9C102.228 129.656 102.228 129.26 101.984 129.016C101.74 128.772 101.344 128.772 101.1 129.016L98.8333 131.283L98.8333 123.208C98.8333 122.863 98.5535 122.583 98.2083 122.583C97.8632 122.583 97.5833 122.863 97.5833 123.208L97.5833 131.283L95.3169 129.016Z"
          fill="white"
        />
      </svg>
    </div>

    <div className="layer-2 rs-down alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle opacity="0.55" cx="98" cy="106" r="6" transform="rotate(-180 98 106)" fill="#4C6B1F" />
      </svg>
    </div>

    <div className="layer-1 rs-down alpha">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle opacity="0.35" cx="98" cy="93" r="3" transform="rotate(-180 98 93)" fill="#4C6B1F" />
      </svg>
    </div>
  </AnimationContainer>
)

export const TileAffordability = () => (
  <AnimationContainer>
    <div className="background">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M184 19C128.773 19 84 63.7729 84 119C84 137.217 88.8741 154.296 97.3867 169.007L131.885 149.09C126.764 140.238 123.831 129.963 123.831 119C123.831 85.7708 150.771 58.831 184 58.831C217.229 58.831 244.169 85.7708 244.169 119C244.169 129.963 241.236 140.238 236.115 149.09L270.613 169.007C279.126 154.296 284 137.217 284 119C284 63.7729 239.23 19 184 19Z"
          fill="#2A2E32"
        />
        <path
          d="M184 19C128.773 19 84 63.7729 84 119C84 137.217 88.8741 154.296 97.3867 169.007L131.885 149.09C126.764 140.238 123.831 129.963 123.831 119C123.831 85.7708 150.771 58.831 184 58.831C217.229 58.831 244.169 85.7708 244.169 119C244.169 123.087 243.762 127.075 242.984 130.931L282.031 138.829C283.321 132.421 284 125.788 284 119C284 63.7729 239.23 19 184 19Z"
          fill="#8E8E8E"
        />
        <path
          d="M141.974 75.9479L114.151 47.4468C103.701 57.6498 95.4765 70.1201 90.2766 84.0668L127.606 97.9797C130.739 89.5892 135.684 82.0846 141.974 75.9479Z"
          fill="#EEB155"
        />
        <path
          d="M97.3873 169.007L131.886 149.09C131.574 148.55 131.272 148.007 130.976 147.457L95.8735 166.295C96.3623 167.208 96.868 168.111 97.3873 169.007Z"
          fill="#FFF4D9"
        />
        <path
          d="M127.61 97.9797L90.2771 84.0669C87.0492 92.7289 84.9821 101.954 84.2761 111.56L123.998 114.523C124.423 108.743 125.665 103.193 127.61 97.9797Z"
          fill="#F5C652"
        />
        <path
          d="M184 58.831C194.957 58.831 205.231 61.7602 214.079 66.8786L233.99 32.3766C219.283 23.8707 202.21 19 184 19C177.259 19 170.674 19.6721 164.303 20.9449L172.147 60.002C175.983 59.2349 179.944 58.831 184 58.831Z"
          fill="#E38A58"
        />
        <path
          d="M172.152 59.9986L164.308 20.9449C145.002 24.8008 127.708 34.2163 114.155 47.4503L141.978 75.9514C150.13 67.9852 160.537 62.3203 172.152 59.9986Z"
          fill="#E89D57"
        />
        <path
          d="M284 119C284 81.9829 263.886 49.6633 233.989 32.3766L214.079 66.8787C232.068 77.2819 244.169 96.7273 244.169 119C244.169 125.738 243.055 132.214 241.016 138.259L278.762 151.011C282.157 140.961 284 130.198 284 119Z"
          fill="#C62E1D"
        />
        <path
          d="M123.831 119C123.831 117.493 123.889 116 123.998 114.523L84.2754 111.56C84.0955 114.017 84.0005 116.498 84.0005 119C84.0005 126.369 84.7981 133.551 86.3119 140.468L125.23 131.915C124.32 127.754 123.831 123.436 123.831 119Z"
          fill="#F8D57F"
        />
        <path
          d="M86.3118 140.468C88.311 149.606 91.5626 158.278 95.8732 166.295L130.976 147.457C128.383 142.634 126.435 137.414 125.23 131.915L86.3118 140.468Z"
          fill="#FBE5AC"
        />
        <path d="M265.173 165.867L270.604 169" stroke="#222222" strokeMiterlimit="10" />
        <path d="M97.3975 69L102.828 72.1362" stroke="#222222" strokeMiterlimit="10" />
        <path d="M184.001 19V25.2691" stroke="#222222" strokeMiterlimit="10" />
        <path d="M134 32.3969L137.137 37.8276" stroke="#222222" strokeMiterlimit="10" />
        <path d="M277.73 119H284" stroke="#222222" strokeMiterlimit="10" />
        <path d="M84.0005 119H90.2696" stroke="#222222" strokeMiterlimit="10" />
        <path d="M265.173 72.1362L270.604 69" stroke="#222222" strokeMiterlimit="10" />
        <path d="M97.3975 169L102.828 165.867" stroke="#222222" strokeMiterlimit="10" />
        <path d="M230.868 37.8276L234.001 32.3969" stroke="#222222" strokeMiterlimit="10" />
        <path
          d="M183.623 187.421C221.226 187.421 251.709 156.738 251.709 118.888C251.709 81.0383 221.226 50.355 183.623 50.355C146.021 50.355 115.538 81.0383 115.538 118.888C115.538 156.738 146.021 187.421 183.623 187.421Z"
          fill="#222222"
        />
      </svg>
    </div>

    <div className="layer-1">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M98.151 80.5754L98.4926 79.8057L205.862 123.32L202.445 131.017L98.151 80.5754Z"
          fill="white"
          className="ta-rotate"
        />
        <path
          d="M195.772 123.218C198.555 116.948 195.729 109.608 189.459 106.825C183.189 104.042 175.85 106.869 173.067 113.139C170.283 119.409 173.11 126.748 179.38 129.531C185.65 132.314 192.989 129.488 195.772 123.218Z"
          fill="white"
        />
      </svg>
    </div>

    <div className="layer-1 alpha ta-up">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M308.695 95.2V93.1375H310.43V95.2H308.695ZM308.695 81.0713V79H310.43V81.0713H308.695ZM309.649 93.7104C308.819 93.7104 308.077 93.5518 307.424 93.2345C306.771 92.9172 306.234 92.4677 305.814 91.886C305.399 91.2984 305.127 90.605 305 89.8059L306.719 89.515C306.885 90.2671 307.239 90.8665 307.781 91.3131C308.324 91.7538 308.974 91.9741 309.732 91.9741C310.485 91.9741 311.097 91.7831 311.567 91.4012C312.043 91.0193 312.281 90.5316 312.281 89.9381C312.281 89.515 312.16 89.1713 311.916 88.9069C311.678 88.6424 311.288 88.4192 310.745 88.237L307.939 87.3203C306.279 86.7739 305.448 85.7162 305.448 84.1473C305.448 83.407 305.614 82.7635 305.946 82.2171C306.284 81.6706 306.757 81.2476 307.366 80.9479C307.975 80.6482 308.686 80.4984 309.5 80.4984C310.28 80.5101 310.972 80.6629 311.576 80.9567C312.185 81.2505 312.688 81.6736 313.087 82.2259C313.491 82.7782 313.77 83.4422 313.925 84.2178L312.157 84.5528C312.079 84.1003 311.916 83.7008 311.667 83.3541C311.423 83.0074 311.113 82.7371 310.737 82.5432C310.366 82.3493 309.948 82.2494 309.483 82.2435C309.046 82.2318 308.653 82.3052 308.304 82.4639C307.961 82.6166 307.687 82.8341 307.482 83.1161C307.283 83.3923 307.184 83.7066 307.184 84.0592C307.184 84.447 307.325 84.7761 307.607 85.0464C307.889 85.3166 308.335 85.5487 308.944 85.7427L311.144 86.4213C312.151 86.7386 312.879 87.1646 313.327 87.6993C313.776 88.2341 314 88.945 314 89.8323C314 90.6079 313.817 91.2866 313.452 91.8683C313.092 92.4442 312.586 92.8966 311.933 93.2257C311.28 93.5489 310.518 93.7104 309.649 93.7104Z"
          fill="#5E5E5E"
        />
        <path
          d="M58.6946 95.2V93.1375H60.4299V95.2H58.6946ZM58.6946 81.0713V79H60.4299V81.0713H58.6946ZM59.6494 93.7104C58.8192 93.7104 58.0775 93.5518 57.4244 93.2345C56.7712 92.9172 56.2343 92.4677 55.8137 91.886C55.3985 91.2984 55.1273 90.605 55 89.8059L56.7186 89.515C56.8847 90.2671 57.2389 90.8665 57.7814 91.3131C58.3238 91.7538 58.9742 91.9741 59.7325 91.9741C60.4852 91.9741 61.0969 91.7831 61.5673 91.4012C62.0434 91.0193 62.2814 90.5316 62.2814 89.9381C62.2814 89.515 62.1596 89.1713 61.9161 88.9069C61.678 88.6424 61.2878 88.4192 60.7454 88.237L57.9391 87.3203C56.2786 86.7739 55.4483 85.7162 55.4483 84.1473C55.4483 83.407 55.6144 82.7635 55.9465 82.2171C56.2841 81.6706 56.7574 81.2476 57.3662 80.9479C57.9751 80.6482 58.6863 80.4984 59.5 80.4984C60.2804 80.5101 60.9723 80.6629 61.5756 80.9567C62.1845 81.2505 62.6882 81.6736 63.0867 82.2259C63.4908 82.7782 63.7703 83.4422 63.9253 84.2178L62.1568 84.5528C62.0793 84.1003 61.9161 83.7008 61.667 83.3541C61.4234 83.0074 61.1135 82.7371 60.7371 82.5432C60.3662 82.3493 59.9483 82.2494 59.4834 82.2435C59.0461 82.2318 58.6531 82.3052 58.3044 82.4639C57.9613 82.6166 57.6873 82.8341 57.4825 83.1161C57.2832 83.3923 57.1836 83.7066 57.1836 84.0592C57.1836 84.447 57.3247 84.7761 57.607 85.0464C57.8893 85.3166 58.3349 85.5487 58.9437 85.7427L61.1439 86.4213C62.1513 86.7386 62.8792 87.1646 63.3275 87.6993C63.7758 88.2341 64 88.945 64 89.8323C64 90.6079 63.8173 91.2866 63.452 91.8683C63.0923 92.4442 62.5858 92.8966 61.9327 93.2257C61.2795 93.5489 60.5185 93.7104 59.6494 93.7104Z"
          fill="#5E5E5E"
        />
      </svg>
    </div>

    <div className="layer-2 alpha ta-up">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          opacity="0.5"
          d="M297.309 102.125V100.836H298.394V102.125H297.309ZM297.309 93.2945V92H298.394V93.2945H297.309ZM297.906 101.194C297.387 101.194 296.923 101.095 296.515 100.897C296.107 100.698 295.771 100.417 295.509 100.054C295.249 99.6865 295.08 99.2531 295 98.7537L296.074 98.5719C296.178 99.042 296.399 99.4166 296.738 99.6957C297.077 99.9711 297.484 100.109 297.958 100.109C298.428 100.109 298.811 99.9895 299.105 99.7507C299.402 99.512 299.551 99.2072 299.551 98.8363C299.551 98.5719 299.475 98.357 299.323 98.1918C299.174 98.0265 298.93 97.887 298.591 97.7731L296.837 97.2002C295.799 96.8587 295.28 96.1976 295.28 95.2171C295.28 94.7544 295.384 94.3522 295.592 94.0107C295.803 93.6691 296.098 93.4047 296.479 93.2174C296.859 93.0301 297.304 92.9365 297.812 92.9365C298.3 92.9438 298.733 93.0393 299.11 93.2229C299.49 93.4066 299.805 93.671 300.054 94.0162C300.307 94.3614 300.481 94.7764 300.578 95.2612L299.473 95.4705C299.425 95.1877 299.323 94.938 299.167 94.7213C299.015 94.5046 298.821 94.3357 298.586 94.2145C298.354 94.0933 298.093 94.0309 297.802 94.0272C297.529 94.0199 297.283 94.0658 297.065 94.1649C296.851 94.2604 296.68 94.3963 296.552 94.5726C296.427 94.7452 296.365 94.9416 296.365 95.162C296.365 95.4044 296.453 95.61 296.629 95.779C296.806 95.9479 297.084 96.093 297.465 96.2142L298.84 96.6383C299.47 96.8366 299.924 97.1029 300.205 97.4371C300.485 97.7713 300.625 98.2157 300.625 98.7702C300.625 99.255 300.511 99.6791 300.283 100.043C300.058 100.403 299.741 100.685 299.333 100.891C298.925 101.093 298.449 101.194 297.906 101.194Z"
          fill="#5E5E5E"
        />
        <path
          opacity="0.5"
          d="M69.3092 102.125V100.836H70.3937V102.125H69.3092ZM69.3092 93.2945V92H70.3937V93.2945H69.3092ZM69.9059 101.194C69.387 101.194 68.9234 101.095 68.5152 100.897C68.107 100.698 67.7714 100.417 67.5085 100.054C67.2491 99.6865 67.0796 99.2531 67 98.7537L68.0741 98.5719C68.1779 99.042 68.3993 99.4166 68.7384 99.6957C69.0774 99.9711 69.4839 100.109 69.9578 100.109C70.4283 100.109 70.8105 99.9895 71.1046 99.7507C71.4021 99.512 71.5509 99.2072 71.5509 98.8363C71.5509 98.5719 71.4747 98.357 71.3225 98.1918C71.1738 98.0265 70.9299 97.887 70.5909 97.7731L68.8369 97.2002C67.7991 96.8587 67.2802 96.1976 67.2802 95.2171C67.2802 94.7544 67.384 94.3522 67.5916 94.0107C67.8026 93.6691 68.0984 93.4047 68.4789 93.2174C68.8594 93.0301 69.304 92.9365 69.8125 92.9365C70.3003 92.9438 70.7327 93.0393 71.1098 93.2229C71.4903 93.4066 71.8051 93.671 72.0542 94.0162C72.3067 94.3614 72.4814 94.7764 72.5783 95.2612L71.473 95.4705C71.4246 95.1877 71.3225 94.938 71.1669 94.7213C71.0146 94.5046 70.8209 94.3357 70.5857 94.2145C70.3539 94.0933 70.0927 94.0309 69.8021 94.0272C69.5288 94.0199 69.2832 94.0658 69.0653 94.1649C68.8508 94.2604 68.6795 94.3963 68.5515 94.5726C68.427 94.7452 68.3647 94.9416 68.3647 95.162C68.3647 95.4044 68.453 95.61 68.6294 95.779C68.8058 95.9479 69.0843 96.093 69.4648 96.2142L70.8399 96.6383C71.4696 96.8366 71.9245 97.1029 72.2047 97.4371C72.4849 97.7713 72.625 98.2157 72.625 98.7702C72.625 99.255 72.5108 99.6791 72.2825 100.043C72.0577 100.403 71.7411 100.685 71.3329 100.891C70.9247 101.093 70.449 101.194 69.9059 101.194Z"
          fill="#5E5E5E"
        />
      </svg>
    </div>

    <div className="layer-3 alpha ta-up">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          opacity="0.35"
          d="M315.847 102.1V101.069H316.715V102.1H315.847ZM315.847 95.0356V94H316.715V95.0356H315.847ZM316.325 101.355C315.91 101.355 315.539 101.276 315.212 101.117C314.886 100.959 314.617 100.734 314.407 100.443C314.199 100.149 314.064 99.8025 314 99.4029L314.859 99.2575C314.942 99.6336 315.119 99.9332 315.391 100.157C315.662 100.377 315.987 100.487 316.366 100.487C316.743 100.487 317.048 100.392 317.284 100.201C317.522 100.01 317.641 99.7658 317.641 99.469C317.641 99.2575 317.58 99.0856 317.458 98.9534C317.339 98.8212 317.144 98.7096 316.873 98.6185L315.47 98.1602C314.639 97.8869 314.224 97.3581 314.224 96.5737C314.224 96.2035 314.307 95.8818 314.473 95.6085C314.642 95.3353 314.879 95.1238 315.183 94.9739C315.488 94.8241 315.843 94.7492 316.25 94.7492C316.64 94.7551 316.986 94.8314 317.288 94.9783C317.592 95.1252 317.844 95.3368 318.043 95.6129C318.245 95.8891 318.385 96.2211 318.463 96.6089L317.578 96.7764C317.54 96.5502 317.458 96.3504 317.333 96.177C317.212 96.0037 317.057 95.8686 316.869 95.7716C316.683 95.6746 316.474 95.6247 316.242 95.6218C316.023 95.6159 315.827 95.6526 315.652 95.7319C315.481 95.8083 315.344 95.917 315.241 96.0581C315.142 96.1961 315.092 96.3533 315.092 96.5296C315.092 96.7235 315.162 96.888 315.304 97.0232C315.445 97.1583 315.667 97.2744 315.972 97.3713L317.072 97.7107C317.576 97.8693 317.94 98.0823 318.164 98.3497C318.388 98.617 318.5 98.9725 318.5 99.4162C318.5 99.804 318.409 100.143 318.226 100.434C318.046 100.722 317.793 100.948 317.466 101.113C317.14 101.274 316.759 101.355 316.325 101.355Z"
          fill="#5E5E5E"
        />
        <path
          opacity="0.35"
          d="M50.8473 102.1V101.069H51.7149V102.1H50.8473ZM50.8473 95.0356V94H51.7149V95.0356H50.8473ZM51.3247 101.355C50.9096 101.355 50.5387 101.276 50.2122 101.117C49.8856 100.959 49.6172 100.734 49.4068 100.443C49.1993 100.149 49.0637 99.8025 49 99.4029L49.8593 99.2575C49.9423 99.6336 50.1195 99.9332 50.3907 100.157C50.6619 100.377 50.9871 100.487 51.3662 100.487C51.7426 100.487 52.0484 100.392 52.2837 100.201C52.5217 100.01 52.6407 99.7658 52.6407 99.469C52.6407 99.2575 52.5798 99.0856 52.458 98.9534C52.339 98.8212 52.1439 98.7096 51.8727 98.6185L50.4696 98.1602C49.6393 97.8869 49.2242 97.3581 49.2242 96.5737C49.2242 96.2035 49.3072 95.8818 49.4732 95.6085C49.6421 95.3353 49.8787 95.1238 50.1831 94.9739C50.4875 94.8241 50.8432 94.7492 51.25 94.7492C51.6402 94.7551 51.9862 94.8314 52.2878 94.9783C52.5923 95.1252 52.8441 95.3368 53.0434 95.6129C53.2454 95.8891 53.3851 96.2211 53.4626 96.6089L52.5784 96.7764C52.5397 96.5502 52.458 96.3504 52.3335 96.177C52.2117 96.0037 52.0567 95.8686 51.8685 95.7716C51.6831 95.6746 51.4742 95.6247 51.2417 95.6218C51.0231 95.6159 50.8266 95.6526 50.6522 95.7319C50.4806 95.8083 50.3436 95.917 50.2412 96.0581C50.1416 96.1961 50.0918 96.3533 50.0918 96.5296C50.0918 96.7235 50.1624 96.888 50.3035 97.0232C50.4446 97.1583 50.6674 97.2744 50.9719 97.3713L52.072 97.7107C52.5756 97.8693 52.9396 98.0823 53.1637 98.3497C53.3879 98.617 53.5 98.9725 53.5 99.4162C53.5 99.804 53.4087 100.143 53.226 100.434C53.0461 100.722 52.7929 100.948 52.4663 101.113C52.1398 101.274 51.7592 101.355 51.3247 101.355Z"
          fill="#5E5E5E"
        />
      </svg>
    </div>

    <div className="layer-2">
      <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          className="ta-scale"
          d="M184.097 122.15V121.119H184.965V122.15H184.097ZM184.097 115.086V114.05H184.965V115.086H184.097ZM184.575 121.405C184.16 121.405 183.789 121.326 183.462 121.167C183.136 121.009 182.867 120.784 182.657 120.493C182.449 120.199 182.314 119.853 182.25 119.453L183.109 119.308C183.192 119.684 183.369 119.983 183.641 120.207C183.912 120.427 184.237 120.537 184.616 120.537C184.993 120.537 185.298 120.442 185.534 120.251C185.772 120.06 185.891 119.816 185.891 119.519C185.891 119.308 185.83 119.136 185.708 119.003C185.589 118.871 185.394 118.76 185.123 118.669L183.72 118.21C182.889 117.937 182.474 117.408 182.474 116.624C182.474 116.254 182.557 115.932 182.723 115.659C182.892 115.385 183.129 115.174 183.433 115.024C183.738 114.874 184.093 114.799 184.5 114.799C184.89 114.805 185.236 114.881 185.538 115.028C185.842 115.175 186.094 115.387 186.293 115.663C186.495 115.939 186.635 116.271 186.713 116.659L185.828 116.826C185.79 116.6 185.708 116.4 185.583 116.227C185.462 116.054 185.307 115.919 185.119 115.822C184.933 115.725 184.724 115.675 184.492 115.672C184.273 115.666 184.077 115.703 183.902 115.782C183.731 115.858 183.594 115.967 183.491 116.108C183.392 116.246 183.342 116.403 183.342 116.58C183.342 116.774 183.412 116.938 183.554 117.073C183.695 117.208 183.917 117.324 184.222 117.421L185.322 117.761C185.826 117.919 186.19 118.132 186.414 118.4C186.638 118.667 186.75 119.023 186.75 119.466C186.75 119.854 186.659 120.193 186.476 120.484C186.296 120.772 186.043 120.998 185.716 121.163C185.39 121.324 185.009 121.405 184.575 121.405Z"
          fill="#222222"
        />
      </svg>
    </div>
  </AnimationContainer>
)

export const TileEV = () => (
  <AnimationContainer>
    <div className="background">
      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="61" r="30" fill="#F3F3F3" className="all-fuels" />
          <path
            d="M107.968 67.4943H92.0324M84.9092 56.0479L84.2345 53.8281C84.1624 53.598 83.9203 53.4599 83.6885 53.5213L81.788 54.002C81.3245 54.1146 81 54.5288 81 55.0045C81 55.5773 81.4687 56.0427 82.0455 56.0427H117.95C118.526 56.0427 118.995 55.5773 118.995 55.0045C118.995 54.5288 118.671 54.1146 118.207 53.9969L116.307 53.5162C116.07 53.4548 115.828 53.5929 115.761 53.823L115.086 56.0427M88.7204 70.0362H84.3167C83.8995 70.0362 83.5647 69.7038 83.5647 69.2895V67.6989C83.5647 67.5864 83.6575 67.4994 83.7656 67.4994H87.3967M108.4 70.0362H115.688C116.106 70.0362 116.44 69.7038 116.44 69.2895V67.6989C116.44 67.5864 116.348 67.4994 116.239 67.4994H112.608M113.274 73.1766C113.274 73.6318 113.645 74 114.103 74H117.312C117.75 74 118.11 73.6676 118.136 73.2328L118.996 61.1727C119.032 60.6254 118.821 60.0884 118.419 59.7099L115.782 57.2242C115.612 57.0657 115.473 56.8713 115.375 56.6616L112.476 50.4218C112.069 49.5575 111.198 49 110.235 49H89.7671C88.8039 49 87.9283 49.5575 87.5266 50.4218L84.6269 56.6565C84.529 56.8662 84.39 57.0606 84.22 57.2191L81.583 59.7048C81.1812 60.0833 80.9701 60.6203 81.0061 61.1676L81.8662 73.2277C81.8971 73.6573 82.2577 73.9949 82.6903 73.9949H85.8991C86.3575 73.9949 86.7283 73.6266 86.7283 73.1714V70.0311H113.274V73.1766ZM83.6421 60.5078L89.8638 61.4284C90.039 61.454 90.1729 61.6074 90.1729 61.7813V63.2083C90.1729 63.4077 90.0132 63.5663 89.8123 63.5663H84.2086C83.6678 63.5663 83.2249 63.1316 83.2249 62.5894V60.8556C83.2249 60.6459 83.4206 60.4771 83.6421 60.5078ZM116.358 60.5078L110.136 61.4284C109.961 61.454 109.827 61.6074 109.827 61.7813V63.2083C109.827 63.4077 109.987 63.5663 110.187 63.5663H115.791C116.332 63.5663 116.775 63.1316 116.775 62.5894V60.8556C116.775 60.6459 116.579 60.4771 116.358 60.5078Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <Label x="100" y="95" dominantBaseline="middle" textAnchor="middle">
            All fuels
          </Label>
        </svg>
      </div>

      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="184" cy="61" r="30" fill="#FF9344" className="phev" />
          <path
            d="M204 58.3837H187.617V63.0864C187.617 67.3415 191.072 70.796 195.327 70.796H196.291C200.546 70.796 204 67.3415 204 63.0864V58.3837Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M193.278 70.519V73.5076C193.278 74.0277 193.7 74.4496 194.22 74.4496H197.397C197.917 74.4496 198.339 74.0277 198.339 73.5076V70.519"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M200.4 51H200.185C199.332 51 198.639 51.693 198.639 52.5461V58.3942H201.946V52.5461C201.946 51.693 201.254 51 200.4 51Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M191.432 51H191.216C190.363 51 189.67 51.693 189.67 52.5461V58.3942H192.978V52.5461C192.978 51.693 192.285 51 191.432 51V51Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M195.808 74.4496V78.3187C195.808 80.1199 194.348 81.5795 192.548 81.5795H188.694C186.892 81.5795 185.433 80.1193 185.433 78.3187C185.433 76.182 183.701 74.4496 181.564 74.4496H180"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M181.016 50.6388L186.454 46.3336L196.283 43.5369C196.573 43.4534 196.74 43.1522 196.656 42.8648L195.942 40.3925C195.858 40.105 195.554 39.938 195.264 40.0215L184.767 43.0085L178.517 47.6847M177.237 69.7941L178.461 64.1856C178.956 61.9214 177.615 59.6572 175.382 58.981C173.088 58.2856 170.654 59.5012 169.846 61.7407L166.086 72.1746C165.867 72.7317 165.236 73.0055 164.674 72.7865L162.165 71.8118C161.604 71.5942 161.329 70.9672 161.549 70.4114L167.329 55.723M166.473 71.1808L173.407 74.1828C174.762 74.7673 176.322 73.9788 176.635 72.5401L178.457 64.176M169.852 72.6428L173.066 63.0631C173.066 63.0631 176.509 59.8434 176.855 59.7366M178.561 63.6942L178.934 63.4067C181.761 61.2397 182.28 57.2082 180.092 54.4074L181.541 53.2014C182.069 52.7606 182.139 51.9763 181.695 51.452L178.836 47.8011C178.41 47.3 177.663 47.2261 177.145 47.6341L175.809 48.6922L174.847 47.6163C174.413 47.1057 173.646 47.0317 173.118 47.4493L166.409 52.6498C165.853 53.0906 165.778 53.9023 166.246 54.4362L171.366 59.6832"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <Label x="184" y="95" dominantBaseline="middle" textAnchor="middle">
            PHEV
          </Label>
        </svg>
      </div>

      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="268" cy="61" r="30" fill="#8E8E8E" className="ice" />
          <path
            d="M268.68 51.2602L261.962 46.2342L250.679 43.0231C250.368 42.9334 250.041 43.1129 249.951 43.422L249.182 46.0796C249.092 46.3888 249.273 46.7129 249.584 46.8026L260.149 49.8092L265.994 54.4363M280.706 59.9012L286.917 75.6872C287.153 76.2856 286.857 76.9587 286.254 77.193L283.558 78.2401C282.955 78.4745 282.277 78.1803 282.041 77.582L277.999 66.3682C277.13 63.9599 274.514 62.6535 272.049 63.4015C269.649 64.1294 268.208 66.5626 268.74 68.9959L270.056 75.0241M268.745 68.9858L270.703 77.9758C271.04 79.5214 272.717 80.3691 274.173 79.7408L281.625 76.5148M270.467 64.2141C270.839 64.3288 274.54 67.7892 274.54 67.7892L277.994 78.0855M276.367 64.1543L281.871 58.515C282.373 57.9416 282.293 57.0691 281.695 56.5954L274.484 51.006C273.917 50.5572 273.094 50.637 272.627 51.1855L271.592 52.3422L270.156 51.2054C269.599 50.7666 268.795 50.8464 268.338 51.3849L265.265 55.309C264.788 55.8724 264.864 56.7151 265.431 57.1887L266.988 58.4851C264.638 61.4967 265.195 65.8297 268.233 68.1582L268.635 68.4673M252.426 63.516C253.345 63.5459 254.234 63.2019 254.882 62.5537C255.529 61.9105 255.881 61.028 255.851 60.1155C255.851 58.5399 253.636 55.0396 252.426 53.6535C251.221 55.0346 249.002 58.5399 249.002 60.1155C248.972 61.028 249.318 61.9105 249.971 62.5537C250.619 63.1969 251.512 63.5459 252.426 63.516Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <Label x="268" y="95" dominantBaseline="middle" textAnchor="middle">
            ICE
          </Label>
        </svg>
      </div>

      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="145" r="30" fill="#607CDE" className="bev" />
          <path d="M114.731 135H80V156H114.731V135Z" stroke="#222222" strokeMiterlimit="10" />
          <path d="M120.775 140.458H114.731V150.79H120.775V140.458Z" stroke="#222222" strokeMiterlimit="10" />
          <path d="M99.9607 139.354H94.7832V152.009H99.9607V139.354Z" stroke="#222222" strokeMiterlimit="10" />
          <path d="M90.0237 139.354H84.8462V152.009H90.0237V139.354Z" stroke="#222222" strokeMiterlimit="10" />
          <path d="M110.216 139.354H105.039V152.009H110.216V139.354Z" stroke="#222222" strokeMiterlimit="10" />
          <Label x="100" y="180" dominantBaseline="middle" textAnchor="middle">
            BEV
          </Label>
        </svg>
      </div>

      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="184" cy="145" r="30" fill="#FFED48" className="hybrid" />
          <path
            d="M204.171 149.6C202.26 158.959 193.996 166 184.091 166C178.135 166 172.774 163.455 169.029 159.392"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M164.343 140.229C166.549 131.493 174.821 125 184.69 125C190.468 125 195.699 127.226 199.486 130.825"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path d="M167.723 142.571L162 151.268H170.2L163.708 160.143" stroke="#222222" strokeMiterlimit="10" />
          <path
            d="M201.828 133.2L205.88 140.229C207.681 143.353 205.43 147.257 201.828 147.257C198.227 147.257 195.976 143.353 197.776 140.229L201.828 133.2V133.2Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <Label x="184" y="180" dominantBaseline="middle" textAnchor="middle">
            Hybrid
          </Label>
        </svg>
      </div>

      <div className="layer-2 ev-up">
        <svg width="368" height="207" viewBox="0 0 368 207" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="268" cy="145" r="30" fill="#97E5F5" className="hfcev" />
          <path
            d="M280.116 128.521C285.49 132.257 288.998 138.412 288.998 145.371C288.998 156.764 279.596 166 267.999 166C256.402 166 247 156.764 247 145.371C247 133.978 256.402 124.742 267.999 124.742C270.499 124.742 272.898 125.171 275.122 125.959"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M267.999 150.724C271.009 150.724 273.449 148.328 273.449 145.371C273.449 142.414 271.009 140.018 267.999 140.018C264.99 140.018 262.55 142.414 262.55 145.371C262.55 148.328 264.99 150.724 267.999 150.724Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <path
            d="M277.85 129.494C279.394 129.494 280.646 128.264 280.646 126.747C280.646 125.23 279.394 124 277.85 124C276.305 124 275.053 125.23 275.053 126.747C275.053 128.264 276.305 129.494 277.85 129.494Z"
            stroke="#222222"
            strokeMiterlimit="10"
          />
          <Label x="268" y="180" dominantBaseline="middle" textAnchor="middle">
            HFCEV
          </Label>
        </svg>
      </div>
    </div>
  </AnimationContainer>
)

const dashboardTileStyles = tv({
  base: "flex relative rounded-md overflow-hidden my-7 flex-col",
  variants: {
    isSearch: {
      true: "my-0",
    },
  },
})

const DashboardTile = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { isSearch?: boolean }>(
  ({ isSearch, className, ...props }, ref) => (
    <div ref={ref} className={cn(dashboardTileStyles({ isSearch }), className)} {...props} />
  ),
)
DashboardTile.displayName = "DashboardTile"

const infoTileStyles = tv({
  base: [
    "bg-black pt-0 px-5 pb-5 text-white absolute w-full rounded-md bottom-0 left-0 z-10 box-border",
    "translate-y-full duration-700 ease-menu",
    "[&>p]:type-body-m [&>p]:my-0 [&>p]:!leading-[150%]",
  ],
  variants: {
    open: {
      true: "translate-y-0",
    },
  },
})

const InfoTile = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { open?: boolean }>(
  ({ open, className, ...props }, ref) => (
    <div ref={ref} className={cn(infoTileStyles({ open }), className)} {...props} />
  ),
)
InfoTile.displayName = "InfoTile"

const labelStyles = tv({
  base: "type-label-caps text-grey fill-grey text-center z-[1] text-[8px]",
})

const Label = forwardRef<SVGTextElement, ComponentPropsWithoutRef<"text">>(({ className, ...props }, ref) => (
  <text ref={ref} className={cn(labelStyles(), className)} {...props} />
))
Label.displayName = "Label"

// The nested layer-1/2/3 + rs-up/rs-down/ta-up/ta-scale/ta-rotate/ev-up
// selectors below are ported 1:1 from the stitches InnerTile: they target
// the raw markup rendered by TileRoadSafety/TileAffordability/TileEV above
// (plain <div className="layer-N ..."> wrappers around inline SVGs), not a
// converted component, so they stay as arbitrary descendant/sibling
// selectors rather than becoming props. `div:nth-of-type(2)` intentionally
// mirrors the original *descendant* (not direct-child) selector.
const innerTileStyles = tv({
  base: [
    "bg-black rounded-md h-full overflow-hidden p-5 flex flex-col gap-2 relative",
    "[&_h5]:text-mid-grey [&_h5]:type-label-caps [&_h5]:my-0",
    "[&_h3]:text-white",

    "[&_.background]:w-full [&_.background]:h-full [&_.background_svg]:w-full [&_.background_svg]:h-full",

    "[&_.layer-1]:absolute [&_.layer-1]:z-[1] [&_.layer-1]:w-full [&_.layer-1]:h-full [&_.layer-1_svg]:w-full [&_.layer-1_svg]:h-full",

    "[&_.layer-2]:absolute [&_.layer-2]:z-[2] [&_.layer-2]:w-full [&_.layer-2]:h-full [&_.layer-2_svg]:w-full [&_.layer-2_svg]:h-full",

    "[&_.layer-2.ev-up]:pointer-events-none",
    "[&_.layer-2.ev-up_svg]:pointer-events-none [&_.layer-2.ev-up_path]:pointer-events-none",
    "[&_.layer-2.ev-up_circle]:[transform-origin:center_center] [&_.layer-2.ev-up_circle]:duration-700 [&_.layer-2.ev-up_circle]:ease-menu [&_.layer-2.ev-up_circle]:pointer-events-auto",
    "[&_.layer-2.ev-up_circle~path]:[transform-origin:center_center] [&_.layer-2.ev-up_circle~path]:duration-700 [&_.layer-2.ev-up_circle~path]:ease-menu",
    "[&_.layer-2.ev-up_circle~text]:[transform-origin:center_center] [&_.layer-2.ev-up_circle~text]:duration-700 [&_.layer-2.ev-up_circle~text]:ease-menu [&_.layer-2.ev-up_circle~text]:opacity-0",
    "[&_.layer-2.ev-up_circle:hover]:-translate-y-[10px] [&_.layer-2.ev-up_circle:hover~path]:-translate-y-[10px] [&_.layer-2.ev-up_circle:hover~text]:opacity-100",

    "[&_.layer-3]:absolute [&_.layer-3]:z-[1] [&_.layer-3]:w-full [&_.layer-3]:h-full [&_.layer-3_svg]:w-full [&_.layer-3_svg]:h-full",

    "[&_.alpha]:opacity-0",

    // `hover:[&_.foo]:class` compiles to `.foo:hover` (hover on the
    // descendant itself), not `.innerTile:hover .foo` — since these
    // absolutely-positioned layer divs stack by z-index, only the topmost
    // one can ever receive that misplaced hover, silently breaking the
    // reveal for every occluded layer. `[&:hover_.foo]` puts :hover on the
    // ancestor (this element) as intended, so hovering anywhere on the
    // tile triggers every layer's animation together.
    "[&:hover_.alpha]:opacity-100",
    "[&:hover_.layer-1.ta-up]:-translate-y-[60px] [&:hover_.layer-1_.ta-rotate]:rotate-[175deg]",
    "[&:hover_.layer-2.ta-up]:-translate-y-[40px] [&:hover_.layer-2_.ta-scale]:scale-[1.8]",
    "[&:hover_.layer-2.ev-scale_circle]:scale-105 [&:hover_.layer-2.ev-scale_path]:scale-105",
    "[&:hover_.layer-3.ta-up]:-translate-y-[20px]",
    // rs-up/rs-down are converged (no offset) by default, separating apart
    // only on hover — the opposite of ta-up's "settled by default, lifts on
    // hover" direction above, but both share the same hover-triggered
    // pattern so they're just as easy to get backwards.
    "[&:hover_.layer-1.rs-up]:-translate-y-[3px] [&:hover_.layer-1.rs-down]:translate-y-[3px]",
    "[&:hover_.layer-2.rs-up]:-translate-y-[6px] [&:hover_.layer-2.rs-down]:translate-y-[6px]",
    "[&:hover_.layer-3.rs-up]:-translate-y-[9px] [&:hover_.layer-3.rs-down]:translate-y-[9px]",

    "[&_div:nth-of-type(2)]:duration-700 [&_div:nth-of-type(2)]:ease-menu [&_div:nth-of-type(2)]:[transform-origin:center_center]",
  ],
  variants: {
    fade: {
      true: "[&_div:nth-of-type(2)]:scale-[0.6] [&_div:nth-of-type(2)]:opacity-0",
    },
  },
})

const InnerTile = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { fade?: boolean }>(
  ({ fade, className, ...props }, ref) => (
    <div ref={ref} className={cn(innerTileStyles({ fade }), className)} {...props} />
  ),
)
InnerTile.displayName = "InnerTile"

// The hover `boxShadow: "$yellow"` from the stitches version is dropped —
// a solitary color isn't a valid <shadow> value, so it was already a no-op
// in the original CSS. The `!important` background swap is the only rule
// with a real visual effect and is preserved below.
const ButtonGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex gap-4",
      "[&_button]:w-1/2 [&_button]:grow [&_a]:w-1/2 [&_a]:grow",
      "[&_button:hover]:!bg-yellow [&_a:hover]:!bg-yellow",
      className,
    )}
    {...props}
  />
))
ButtonGroup.displayName = "ButtonGroup"

const AnimationContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex grow items-center justify-center relative",
        "[&>div]:duration-700 [&>div]:ease-menu",
        "[&_.background]:relative [&_.background]:z-0",
        "[&_.ta-scale]:duration-700 [&_.ta-scale]:ease-menu [&_.ta-scale]:[transform-origin:50%_57%]",
        "[&_.ta-rotate]:duration-700 [&_.ta-rotate]:ease-menu [&_.ta-rotate]:[transform-origin:center_57%]",
        className,
      )}
      {...props}
    />
  ),
)
AnimationContainer.displayName = "AnimationContainer"

const Info = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<"button">>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "absolute top-3 right-3 w-6 h-6 text-white flex items-center justify-center rounded-[8px] duration-700 ease-menu bg-transparent z-50 hover:bg-dark-grey",
      className,
    )}
    {...props}
  />
))
Info.displayName = "Info"
